import type { UUID } from '@aline/common';
import { link, params } from '@aline/routes';
import { format, parseISO } from 'date-fns';
import type { MentionUserFilter } from 'src/components/ai/chat/ChatPanel/AlineMarkdownEditor/types';
import { Apollo } from 'src/gql/generated';

type AssignmentAccount =
  | {
      __typename: 'User';
      email: string;
      id: UUID;
      firstName?: string | null;
      lastName?: string | null;
    }
  | { __typename: 'Org' };

export type ReviewUpload = Pick<
  Apollo.TaskUploadFieldsFragment,
  'createdAt' | 'documentId' | 'fileName' | 'id' | 'status'
>;

export interface ReviewAssignment {
  decidedAt: string | null;
  decision: Apollo.TaskAssigneeDecision | null;
  isExternal: boolean;
  isRequired: boolean;
  permission: { account?: AssignmentAccount | null };
  /** When this reviewer was asked to review; asked with no decision means the task waits on them. */
  reviewRequestedAt: string | null;
  role: Apollo.TaskAssigneeRole;
  // Versions this person uploaded through the task. A pending one records no decision on its own.
  uploads: readonly ReviewUpload[];
}

export type TaskDecisionParam =
  | typeof params.taskDecision.complete
  | typeof params.taskDecision.remind
  | typeof params.taskDecision.requestChanges;

export const TASK_TURN = {
  ChangesRequested: 'changesRequested',
  Done: 'done',
  InReview: 'inReview',
  NotStarted: 'notStarted',
  Rejected: 'rejected',
  WithAssignees: 'withAssignees',
} as const;
type TaskTurnKind = (typeof TASK_TURN)[keyof typeof TASK_TURN];

/** Whose move it is on a task, and who they are. */
interface TaskTurn {
  kind: TaskTurnKind;
  /** Who the task is with: its assignees, or its reviewers while in review. */
  names: string[];
  /** The reviewers whose request for changes is standing. */
  requestedByNames: string[];
}

export const FOOTER_ACTION = {
  Approve: 'approve',
  Complete: 'complete',
  MarkComplete: 'markComplete',
  Reject: 'reject',
  Remind: 'remind',
  RemindReviewers: 'remindReviewers',
  RequestChanges: 'requestChanges',
  RequestReview: 'requestReview',
  Start: 'start',
} as const;
export type FooterAction = (typeof FOOTER_ACTION)[keyof typeof FOOTER_ACTION];

export const FOOTER_ACTION_LABEL = {
  [FOOTER_ACTION.Approve]: 'Approve',
  [FOOTER_ACTION.Complete]: 'Complete',
  [FOOTER_ACTION.MarkComplete]: 'Mark Complete',
  [FOOTER_ACTION.Reject]: 'Reject Task',
  [FOOTER_ACTION.Remind]: 'Remind',
  [FOOTER_ACTION.RemindReviewers]: 'Remind',
  [FOOTER_ACTION.RequestChanges]: 'Submit Changes',
  [FOOTER_ACTION.RequestReview]: 'Request Review',
  [FOOTER_ACTION.Start]: 'Start',
} satisfies Record<FooterAction, string>;

export const TASK_STATUS_LABEL = {
  [Apollo.TaskStatus.ChangesRequested]: 'Changes requested',
  [Apollo.TaskStatus.Completed]: 'Completed',
  [Apollo.TaskStatus.InReview]: 'In review',
  [Apollo.TaskStatus.NotStarted]: 'Not started',
  [Apollo.TaskStatus.Rejected]: 'Rejected',
  [Apollo.TaskStatus.Started]: 'In progress',
  [Apollo.TaskStatus.Template]: 'Template',
} satisfies Record<Apollo.TaskStatus, string>;

const TASK_ROLE_LABEL = {
  [Apollo.TaskAssigneeRole.Assignee]: 'Assignee',
  [Apollo.TaskAssigneeRole.Reviewer]: 'Reviewer',
} satisfies Record<Apollo.TaskAssigneeRole, string>;

/** Reviewers need someone doing the work: an assignee row, or Aline AI. */
export const REVIEWER_NEEDS_ASSIGNEE_REASON =
  "Add another assignee first. Reviewers check someone's work.";

const MAX_LISTED_NAMES = 3;

const DECISION_PARAM_BY_ACTION: Partial<Record<FooterAction, TaskDecisionParam>> = {
  [FOOTER_ACTION.Approve]: params.taskDecision.complete,
  [FOOTER_ACTION.Complete]: params.taskDecision.complete,
  [FOOTER_ACTION.Remind]: params.taskDecision.remind,
  [FOOTER_ACTION.RemindReviewers]: params.taskDecision.remind,
  [FOOTER_ACTION.RequestChanges]: params.taskDecision.requestChanges,
  [FOOTER_ACTION.RequestReview]: params.taskDecision.complete,
};

const TURN_KIND_BY_STATUS = {
  [Apollo.TaskStatus.ChangesRequested]: TASK_TURN.ChangesRequested,
  [Apollo.TaskStatus.Completed]: TASK_TURN.Done,
  [Apollo.TaskStatus.InReview]: TASK_TURN.InReview,
  [Apollo.TaskStatus.NotStarted]: TASK_TURN.NotStarted,
  [Apollo.TaskStatus.Rejected]: TASK_TURN.Rejected,
  [Apollo.TaskStatus.Started]: TASK_TURN.WithAssignees,
  [Apollo.TaskStatus.Template]: null,
} satisfies Record<Apollo.TaskStatus, TaskTurnKind | null>;

const TURN_TEXT = {
  [TASK_TURN.ChangesRequested]: ({ requestedByNames }) =>
    requestedByNames.length
      ? `Changes requested by ${formatNameList(requestedByNames)}`
      : 'Changes requested',
  [TASK_TURN.Done]: () => 'Done',
  [TASK_TURN.InReview]: ({ names }) =>
    names.length ? `In review with ${formatNameList(names)}` : 'In review',
  [TASK_TURN.NotStarted]: ({ names }) =>
    names.length ? `To do · with ${formatNameList(names)}` : 'To do',
  [TASK_TURN.Rejected]: () => 'Rejected',
  [TASK_TURN.WithAssignees]: ({ names }) =>
    names.length ? `With ${formatNameList(names)}` : 'In progress',
} satisfies Record<TaskTurnKind, (turn: TaskTurn) => string>;

/** A document opened for review: the editor shows the task's review bar. */
export function getReviewDocumentLink(documentId: UUID, taskId: UUID): string {
  return `${link.DocumentDetail({ documentId })}?${params.reviewTaskId}=${taskId}`;
}

/** The task page as its external assignees see it, for an internal user to check. */
export function getTaskPreviewLink(taskId: UUID): string {
  return `${link.TaskLanding({ taskId })}?${params.previewAsExternal}=1`;
}

/** The workflow's documents a task reviews, in workflow order, without the signing certificate. */
export function getTaskDocuments<TDocument>(task: {
  workflow: { files: readonly { document: TDocument; orderId: string }[] };
}): TDocument[] {
  return task.workflow.files
    .filter(file => file.orderId !== 'certificate')
    .map(file => file.document);
}

/** The task page, opening the dialog for a decision made from a document's review bar. */
export function getTaskDecisionLink(taskId: UUID, decision: TaskDecisionParam): string {
  return `${link.TaskLanding({ taskId })}?${params.taskDecision.key}=${decision}`;
}

/** What a standing decision reads as, through the role of the row that made it. */
export function getDecisionLabel(options: {
  decision: Apollo.TaskAssigneeDecision;
  role: Apollo.TaskAssigneeRole;
}): string {
  const { decision, role } = options;
  if (decision === Apollo.TaskAssigneeDecision.ChangesRequested) return 'Changes requested';
  if (decision === Apollo.TaskAssigneeDecision.Rejected) return 'Rejected';
  return role === Apollo.TaskAssigneeRole.Reviewer ? 'Approved' : 'Completed';
}

/** The muted line under a name: "Reviewer · External". Roles are only worth naming with reviewers. */
export function getAssignmentRoleText(options: {
  hasReviewers: boolean;
  isExternal: boolean;
  role: Apollo.TaskAssigneeRole;
}): string | null {
  const { hasReviewers, isExternal, role } = options;
  const parts = [
    ...(hasReviewers ? [TASK_ROLE_LABEL[role]] : []),
    ...(isExternal ? ['External'] : []),
  ];
  return parts.length ? parts.join(' · ') : null;
}

interface AssignmentUser {
  email: string;
  id: UUID;
  name: string;
}

/** The person behind an assignment, or null for a role assignment. */
export function getAssignmentUser(
  assignment: Pick<ReviewAssignment, 'permission'>
): AssignmentUser | null {
  const { account } = assignment.permission;
  if (account?.__typename !== 'User') return null;
  const fullName = [account.firstName, account.lastName].filter(Boolean).join(' ');
  return { email: account.email, id: account.id, name: fullName || account.email };
}

export function getRequesterAssignment<TAssignment extends Pick<ReviewAssignment, 'permission'>>(
  assignments: readonly TAssignment[],
  userId: UUID | null
): TAssignment | null {
  if (!userId) return null;
  return assignments.find(assignment => getAssignmentUser(assignment)?.id === userId) ?? null;
}

/** Whether anyone besides the viewer has a decision on the task that a reopen could keep or clear. */
export function getHasOthersDecisions(
  assignments: readonly Pick<ReviewAssignment, 'decision' | 'permission'>[],
  userId: UUID | null
): boolean {
  return assignments.some(
    assignment => Boolean(assignment.decision) && getAssignmentUser(assignment)?.id !== userId
  );
}

export function getIsReviewerRow(assignment: Pick<ReviewAssignment, 'role'>): boolean {
  return assignment.role === Apollo.TaskAssigneeRole.Reviewer;
}

export function getHasReviewers(assignments: readonly Pick<ReviewAssignment, 'role'>[]): boolean {
  return assignments.some(getIsReviewerRow);
}

/** A reviewer who was asked to review and has not answered yet. */
export function getIsReviewPending(
  assignment: Pick<ReviewAssignment, 'decision' | 'reviewRequestedAt' | 'role'>
): boolean {
  return (
    getIsReviewerRow(assignment) && Boolean(assignment.reviewRequestedAt) && !assignment.decision
  );
}

/** Some reviewer was asked and has not answered, so the task is in review. */
export function getHasOpenReviewRequest(
  assignments: readonly Pick<ReviewAssignment, 'decision' | 'reviewRequestedAt' | 'role'>[]
): boolean {
  return assignments.some(getIsReviewPending);
}

/**
 * Some reviewer was asked or has answered. Until then an assignee's Complete asks the reviewers;
 * after, reviewers are asked again one by one from their rows.
 */
export function getHasReviewBegun(
  assignments: readonly Pick<ReviewAssignment, 'decision' | 'reviewRequestedAt' | 'role'>[]
): boolean {
  return assignments.some(
    assignment =>
      getIsReviewerRow(assignment) &&
      (Boolean(assignment.reviewRequestedAt) || Boolean(assignment.decision))
  );
}

/**
 * Why an assignee can't complete a task under review yet: a required reviewer hasn't approved.
 * An external viewer isn't told the internal side's names.
 */
export function getCompleteBlockedReason(
  assignments: readonly Pick<
    ReviewAssignment,
    'decision' | 'isExternal' | 'isRequired' | 'permission' | 'role'
  >[],
  options: { isExternalView: boolean }
): string | null {
  const waitingOn = assignments.filter(
    assignment =>
      getIsReviewerRow(assignment) &&
      assignment.isRequired &&
      assignment.decision !== Apollo.TaskAssigneeDecision.Completed
  );
  if (!waitingOn.length) return null;
  const names = getVisibleNames(waitingOn, options.isExternalView);
  if (!names) return 'Waiting on the required reviewers to approve.';
  return `Waiting on ${formatNameList(names)} to approve.`;
}

export const COMPLETE_ASSIGNEES_ONLY_REASON = 'Only task assignees can complete this task';

export const APPROVE_UNSUBMITTED_REASON =
  'Submit your tracked changes and comments for review first.';

/** Shown while the markup check has not answered, so Approve is not offered on an unknown. */
export const APPROVE_REVIEW_PENDING_REASON = 'Checking your markup.';

/**
 * Why a reviewer's Approve waits: they marked up the documents this round and haven't submitted
 * it, or that check has not answered yet. Approving would hand the assignees markup nobody asked
 * them to look at. `review` is undefined until the check returns, including when it fails.
 */
export function getApproveBlockedReason(
  review:
    | Pick<Apollo.TaskUnsubmittedReview, 'commentCount' | 'trackedChangeCount'>
    | null
    | undefined,
  options: { liveTrackedChangeCount: number } = { liveTrackedChangeCount: 0 }
): string | null {
  const hasMarkup =
    options.liveTrackedChangeCount > 0 ||
    Boolean(review && (review.commentCount || review.trackedChangeCount));
  if (hasMarkup) return APPROVE_UNSUBMITTED_REASON;
  if (review === undefined) return APPROVE_REVIEW_PENDING_REASON;
  return null;
}

/**
 * Why the footer's Complete is disabled: the viewer is not an assignee, or a
 * required reviewer has not approved yet.
 */
export function getFooterCompleteBlockedReason(options: {
  assignments: readonly Pick<
    ReviewAssignment,
    'decision' | 'isExternal' | 'isRequired' | 'permission' | 'reviewRequestedAt' | 'role'
  >[];
  isExternalView: boolean;
  viewer: Pick<ReviewAssignment, 'role'> | null;
}): string | null {
  if (!options.viewer) return COMPLETE_ASSIGNEES_ONLY_REASON;
  if (options.viewer.role !== Apollo.TaskAssigneeRole.Assignee) return null;
  if (!getHasReviewBegun(options.assignments)) return null;
  return getCompleteBlockedReason(options.assignments, { isExternalView: options.isExternalView });
}

/**
 * The assignees' line once review lets them finish: some reviewer approved and no required one is
 * still to. Approving never completes the task, so this tells them the next move is theirs. An
 * external viewer isn't told the internal side's names. Null until then.
 */
export function getReadyToCompleteText(
  assignments: readonly Pick<
    ReviewAssignment,
    'decision' | 'isExternal' | 'isRequired' | 'permission' | 'role'
  >[],
  options: { isExternalView: boolean }
): string | null {
  const approved = assignments.filter(
    assignment =>
      getIsReviewerRow(assignment) && assignment.decision === Apollo.TaskAssigneeDecision.Completed
  );
  if (!approved.length || getCompleteBlockedReason(assignments, options)) return null;
  const names = getVisibleNames(approved, options.isExternalView);
  const who = names ? formatNameList(names) : 'The reviewers';
  return `${who} approved. Complete the task to finish it.`;
}

/**
 * The warning on an assignee's Complete when optional reviewers have not approved, whether they
 * are reviewing, have changes standing or were never asked: completing finishes the task without
 * them, and the server emails them that it did.
 */
export function getOptionalReviewsOutstandingWarning(
  assignments: readonly Pick<
    ReviewAssignment,
    'decision' | 'isExternal' | 'isRequired' | 'permission' | 'reviewRequestedAt' | 'role'
  >[],
  options: { isExternalView: boolean }
): string | null {
  const outstanding = assignments.filter(
    assignment =>
      getIsReviewerRow(assignment) &&
      !assignment.isRequired &&
      assignment.decision !== Apollo.TaskAssigneeDecision.Completed
  );
  if (!outstanding.length) return null;
  const names = getVisibleNames(outstanding, options.isExternalView);
  const consequence = 'Completing now finishes the task without their review.';
  if (!names) return `Some optional reviewers haven't approved. ${consequence}`;
  const verb = names.length === 1 ? "hasn't" : "haven't";
  return `${formatNameList(names)} ${verb} approved. ${consequence}`;
}

/**
 * The reviewers an assignee can ask again from the footer, once some reviewer has requested
 * changes: every reviewer not already reviewing. Those who requested changes are asked by
 * default. Empty when nobody has requested changes.
 */
export function getReReviewCandidates<
  TAssignment extends Pick<ReviewAssignment, 'decision' | 'reviewRequestedAt' | 'role'>,
>(assignments: readonly TAssignment[]): { assignment: TAssignment; isDefault: boolean }[] {
  const hasChangesRequested = assignments.some(
    assignment =>
      getIsReviewerRow(assignment) &&
      assignment.decision === Apollo.TaskAssigneeDecision.ChangesRequested
  );
  if (!hasChangesRequested) return [];

  return assignments
    .filter(assignment => getIsReviewerRow(assignment) && !getIsReviewPending(assignment))
    .map(assignment => ({
      assignment,
      isDefault: assignment.decision === Apollo.TaskAssigneeDecision.ChangesRequested,
    }));
}

/** "We'll email Kim and Lee a reminder to review." */
export function getReviewReminderMessage(names: readonly string[]): string {
  return `We'll email ${formatNameList(names)} a reminder to review.`;
}

/** "Kim and Lee are asked to review again, which starts the next round." */
export function getReReviewMessage(names: readonly string[]): string {
  const verb = names.length === 1 ? 'is' : 'are';
  return `${formatNameList(names)} ${verb} asked to review again, which starts the next round.`;
}

/**
 * The task as the preview's stand-in external first sees it. An external reviewer is invited once
 * they are asked to review, so until then the preview shows them asked and the task in review. A
 * task in a workflow template is shown as the started task it becomes when the workflow launches,
 * so the preview has the activity and action bar a template keeps hidden.
 */
export function getExternalPreviewTask<
  TTask extends {
    assignments: readonly (Pick<ReviewAssignment, 'isExternal' | 'reviewRequestedAt' | 'role'> & {
      permission: { id: UUID };
    })[];
    startedAt: string | null;
    status: Apollo.TaskStatus;
    subtasks: readonly { assignments: readonly { permission: { id: UUID } }[] }[];
    variant: Apollo.TaskVariant;
  },
>(task: TTask): TTask {
  const now = new Date().toISOString();
  const standIn = task.assignments.find(assignment => assignment.isExternal);
  // An external assignee sees only the sub-tasks they are assigned to.
  const subtasks = task.subtasks.filter(subtask =>
    subtask.assignments.some(assignment => assignment.permission.id === standIn?.permission.id)
  );
  const launched: TTask =
    task.status === Apollo.TaskStatus.Template
      ? {
          ...task,
          startedAt: now,
          status: Apollo.TaskStatus.Started,
          subtasks,
          variant: Apollo.TaskVariant.Document,
        }
      : { ...task, subtasks };
  const isBeforeReview =
    launched.status === Apollo.TaskStatus.NotStarted ||
    launched.status === Apollo.TaskStatus.Started;
  if (!standIn || !getIsReviewerRow(standIn) || !isBeforeReview) return launched;
  return {
    ...launched,
    assignments: launched.assignments.map(assignment =>
      assignment === standIn ? { ...assignment, reviewRequestedAt: now } : assignment
    ),
    status: Apollo.TaskStatus.InReview,
  };
}

/** Someone does the work: an assignee row, or Aline AI. */
export function getHasAssigneeSide(task: {
  assignments: readonly Pick<ReviewAssignment, 'role'>[];
  isDelegatedToAi: boolean;
}): boolean {
  return task.isDelegatedToAi || task.assignments.some(assignment => !getIsReviewerRow(assignment));
}

/** Assignee rows first, then reviewer rows, each side in its given order. */
export function sortAssignmentsByRole<TAssignment extends Pick<ReviewAssignment, 'role'>>(
  assignments: readonly TAssignment[]
): TAssignment[] {
  return [
    ...assignments.filter(assignment => !getIsReviewerRow(assignment)),
    ...assignments.filter(getIsReviewerRow),
  ];
}

export function getTaskIsOpen(task: { completedAt: string | null; rejectedAt: string | null }) {
  return !task.completedAt && !task.rejectedAt;
}

// Any internal assignee or reviewer, required or not, may reject, which closes the task. Externals never can.
export function getCanRejectTask(options: {
  assignment: Pick<ReviewAssignment, 'isRequired' | 'role'> | null;
  requesterIsInternal: boolean;
}): boolean {
  return Boolean(options.assignment) && options.requesterIsInternal;
}

interface TurnTask {
  assignments: readonly ReviewAssignment[];
  counterpartyName: string | null;
  isDelegatedToAi: boolean;
  status: Apollo.TaskStatus;
}

/**
 * Whose move it is. While a side still owes a decision, the names are the ones who haven't made
 * it; once they all have, the whole side is named.
 */
export function getTaskTurn(task: TurnTask, viewerUserId: UUID | null): TaskTurn | null {
  const kind = TURN_KIND_BY_STATUS[task.status];
  if (!kind) return null;
  const nameOf = (assignment: ReviewAssignment) =>
    getTurnName(assignment, { counterpartyName: task.counterpartyName, viewerUserId });
  const assignees = task.assignments.filter(assignment => !getIsReviewerRow(assignment));
  const reviewers = task.assignments.filter(getIsReviewerRow);
  const assigneeNames = getPendingOrAll(assignees, assignment => !assignment.decision).flatMap(
    nameOf
  );
  const names =
    kind === TASK_TURN.InReview
      ? getPendingOrAll(reviewers, getIsReviewPending).flatMap(nameOf)
      : assigneeNames;
  const requestedByNames = reviewers
    .filter(assignment => assignment.decision === Apollo.TaskAssigneeDecision.ChangesRequested)
    .flatMap(nameOf);
  const withAi = !assignees.length && task.isDelegatedToAi && kind !== TASK_TURN.InReview;
  return { kind, names: withAi ? ['Aline AI'] : names, requestedByNames };
}

/** "With Sam", "In review with Jane (Acme)", "Changes requested by Jane". */
export function getTaskTurnText(turn: TaskTurn): string {
  return TURN_TEXT[turn.kind](turn);
}

export interface FooterActionsInput {
  canEdit: boolean;
  isInternal: boolean;
  isOpen: boolean;
  /** The task's phase has started; always true once a phaseless workflow launches. */
  isPhaseActive: boolean;
  isPhased: boolean;
  isStarted: boolean;
  task: Pick<TurnTask, 'assignments' | 'isDelegatedToAi'>;
  viewer: Pick<
    ReviewAssignment,
    'decision' | 'isRequired' | 'reviewRequestedAt' | 'role' | 'uploads'
  > | null;
}

/**
 * The viewer's buttons, primary first. An assignee finishes (Complete, or Request review with
 * reviewers) and, while reviewers are reviewing, can remind them all. A reviewer who was asked
 * approves or submits changes, and one who uploaded files can only submit changes. A reviewer whose
 * changes stand can only remind the assignees until they ask again. An internal editor off the
 * task sees a disabled Complete so the footer is still there, and can still Submit changes or
 * Mark complete a task nobody is on. Once the viewer has made their
 * decision, or the reviewers are reviewing, there are no buttons: the footer shows that
 * with an undo instead.
 */
export function getFooterActions(input: FooterActionsInput): FooterAction[] {
  const { canEdit, isInternal, isOpen, isStarted, task, viewer } = input;
  if (!isOpen) return [];
  const canStart =
    !isStarted && (!input.isPhased || input.isPhaseActive) && (canEdit || Boolean(viewer));
  const start = canStart ? [FOOTER_ACTION.Start] : [];
  const reject =
    isStarted && getCanRejectTask({ assignment: viewer, requesterIsInternal: isInternal })
      ? [FOOTER_ACTION.Reject]
      : [];

  if (viewer?.decision === Apollo.TaskAssigneeDecision.Completed) return [];
  if (viewer && getIsReviewerRow(viewer)) {
    if (viewer.decision === Apollo.TaskAssigneeDecision.ChangesRequested)
      return [FOOTER_ACTION.Remind, ...start, ...reject];
    if (getPendingUploads(viewer).length)
      return [FOOTER_ACTION.RequestChanges, ...start, ...reject];
    if (!viewer.reviewRequestedAt) return [...start, ...reject];
    return [FOOTER_ACTION.Approve, FOOTER_ACTION.RequestChanges, ...start, ...reject];
  }
  if (viewer) {
    const remind = getHasOpenReviewRequest(task.assignments) ? [FOOTER_ACTION.RemindReviewers] : [];
    return [getAssigneeFinish(task.assignments), ...remind, ...start, ...reject];
  }
  const offTaskComplete = isStarted ? [FOOTER_ACTION.Complete] : [];
  if (task.assignments.length)
    return isInternal
      ? [...offTaskComplete, FOOTER_ACTION.RequestChanges, ...start]
      : [...offTaskComplete, ...start];
  if (task.isDelegatedToAi) return [...offTaskComplete, ...start];
  return [...start, FOOTER_ACTION.MarkComplete];
}

/** Why this footer action is disabled while the rest stay live, or null. */
export function getFooterActionBlockedReason(
  action: FooterAction,
  reasons: { approveBlockedReason: string | null; completeBlockedReason: string | null }
): string | null {
  if (action === FOOTER_ACTION.Approve) return reasons.approveBlockedReason;
  if (action === FOOTER_ACTION.Complete) return reasons.completeBlockedReason;
  return null;
}

/** The Request re-review button beside Complete, which is not a footer action of its own. */
export const RE_REVIEW_FILL = 'requestReReview';
export type FooterFill = FooterAction | typeof RE_REVIEW_FILL;

/**
 * The one filled button: the first action, unless Complete is blocked, when the fill moves to what
 * unblocks it. Request re-review wins over Remind reviewers, since a reviewer who asked for changes
 * is waiting on the assignees. Approve keeps the fill while the reviewer's own markup is
 * unsubmitted; it is disabled, not demoted.
 */
export function getPrimaryFooterAction(options: {
  actions: readonly FooterAction[];
  hasReReview: boolean;
  isCompleteBlocked: boolean;
}): FooterFill | undefined {
  const { actions, hasReReview, isCompleteBlocked } = options;
  if (isCompleteBlocked && hasReReview) return RE_REVIEW_FILL;
  if (isCompleteBlocked && actions.includes(FOOTER_ACTION.RemindReviewers))
    return FOOTER_ACTION.RemindReviewers;
  return actions.at(0);
}

/** The decision a document's review bar hands to the task page for an action, if it has one. */
export function getDecisionParamForAction(action: FooterAction): TaskDecisionParam | null {
  return DECISION_PARAM_BY_ACTION[action] ?? null;
}

/** The viewer's action a decision handed off from a document's review bar opens, if they have it. */
export function getActionForDecisionParam(
  decision: string | null,
  actions: readonly FooterAction[]
): FooterAction | null {
  if (!decision) return null;
  return actions.find(action => getDecisionParamForAction(action) === decision) ?? null;
}

/** "Sam and Dana will be notified.", from the people the server says the action reaches. */
export function getNotifiedText(names: readonly string[]): string | null {
  if (!names.length) return null;
  return `${formatNameList(names)} will be notified.`;
}

/**
 * Who the @ menu offers in a task's activity lane. The Team lane offers anyone outside the
 * external side; the External lane offers only people on the task.
 */
export function getLaneMentionFilter(options: {
  audience: Apollo.ActivityAudience;
  externalUserIds: ReadonlySet<UUID>;
  taskUserIds: ReadonlySet<UUID>;
  viewerOrgId: UUID | null;
}): MentionUserFilter {
  const { audience, externalUserIds, taskUserIds, viewerOrgId } = options;
  if (audience === Apollo.ActivityAudience.External) return user => taskUserIds.has(user.id);
  return user => !externalUserIds.has(user.id) && (!viewerOrgId || user.org?.id === viewerOrgId);
}

/** The uploads this person has not sent yet, oldest first. */
export function getPendingUploads<TUpload extends Pick<ReviewUpload, 'status'>>(assignment: {
  uploads: readonly TUpload[];
}): TUpload[] {
  return assignment.uploads.filter(upload => upload.status === Apollo.TaskUploadStatus.Pending);
}

export interface YourNextStep {
  detail: string;
  severity: 'info' | 'success';
  title: string;
}

/** The alert at the top of an external's task page, built from their row and the task's state. */
export function getYourNextStep(options: {
  assignment: Pick<
    ReviewAssignment,
    'decidedAt' | 'decision' | 'reviewRequestedAt' | 'role' | 'uploads'
  > | null;
  documentCount: number;
  hasReviewers: boolean;
  ownerName: string;
  /** The reviewers' approvals let the assignees finish; see {@link getReadyToCompleteText}. */
  readyToCompleteText: string | null;
  status: Apollo.TaskStatus;
}): YourNextStep | null {
  const { assignment, ownerName, readyToCompleteText, status } = options;
  if (!assignment) return null;
  if (status === Apollo.TaskStatus.Rejected)
    return {
      detail: 'Nothing is needed from you.',
      severity: 'info',
      title: 'This task was closed',
    };
  if (status === Apollo.TaskStatus.Completed)
    return {
      detail: 'Nothing is needed from you.',
      severity: 'success',
      title: 'This task is done',
    };

  const isReviewer = getIsReviewerRow(assignment);
  if (assignment.decision === Apollo.TaskAssigneeDecision.Completed)
    return {
      detail: 'Nothing else is needed from you.',
      severity: 'success',
      title: getYouFinishedText({ decidedAt: assignment.decidedAt, isReviewer }),
    };
  if (isReviewer) return getReviewerNextStep({ ...options, assignment });
  if (readyToCompleteText && status !== Apollo.TaskStatus.ChangesRequested)
    return { detail: readyToCompleteText, severity: 'info', title: 'Ready to complete' };
  if (status === Apollo.TaskStatus.InReview)
    return {
      detail: `Nothing else is needed from you unless ${ownerName} asks for changes.`,
      severity: 'success',
      title: 'Sent for review',
    };
  if (status === Apollo.TaskStatus.ChangesRequested)
    return {
      detail: 'Read their note below, make the changes, then Request Re-Review.',
      severity: 'info',
      title: `${ownerName} requested changes`,
    };
  return {
    detail: options.hasReviewers
      ? 'Do the work below, then Request review.'
      : 'Do the work below, then Complete.',
    severity: 'info',
    title: `${ownerName} asked you to complete this task`,
  };
}

/**
 * The footer's line once the viewer has uploaded files but not sent their decision: an upload
 * only saves a new version, so nobody hears about it until they click the button this names. Null
 * when nothing they uploaded is waiting.
 */
export function getUploadedFilesNotice(options: {
  assignment: Pick<ReviewAssignment, 'decision' | 'role' | 'uploads'> | null;
  hasReviewers: boolean;
  ownerName: string;
  status: Apollo.TaskStatus;
}): string | null {
  const { assignment, hasReviewers, ownerName, status } = options;
  if (!assignment || assignment.decision) return null;
  const count = getPendingUploads(assignment).length;
  if (!count) return null;
  if (status === Apollo.TaskStatus.Completed || status === Apollo.TaskStatus.Rejected) return null;

  const isReviewer = getIsReviewerRow(assignment);
  // An assignee's upload is already on its way once they have asked for review.
  if (!isReviewer && status === Apollo.TaskStatus.InReview) return null;

  const uploaded = `You uploaded ${count === 1 ? '1 file' : `${count} files`}.`;
  if (isReviewer) return `${uploaded} ${ownerName} isn't notified until you submit changes.`;
  const finish = getUploadedAssigneeFinishLabel({ hasReviewers, status });
  return `${uploaded} ${ownerName} isn't notified until you click ${finish}.`;
}

/** "You requested changes · Mar 3" */
export function getYouRequestedChangesText(decidedAt: string | null): string {
  if (!decidedAt) return 'You requested changes';
  return `You requested changes · ${formatDay(decidedAt)}`;
}

/** "You approved on Mar 3", or "You completed on Mar 3" for an assignee. */
export function getYouFinishedText(options: {
  decidedAt: string | null;
  isReviewer: boolean;
}): string {
  const verb = options.isReviewer ? 'approved' : 'completed';
  return `You ${verb}${onDay(options.decidedAt) || ' this'}`;
}

/**
 * The tooltip on a row's state icon: "Approved by Jane on Mar 3", "Reviewing. Sam requested review
 * on Mar 3". An assignee on a finished task with reviewers reads the task's completion when they
 * recorded none of their own.
 */
export function getAssignmentStateText(options: {
  decidedAt: string | null;
  decision: Apollo.TaskAssigneeDecision | null;
  name: string;
  requestedText: string | null;
  role: Apollo.TaskAssigneeRole;
  taskCompletedAt: string | null;
}): string {
  const { decidedAt, decision, name, requestedText, role, taskCompletedAt } = options;
  if (decision) return `${getDecisionLabel({ decision, role })} by ${name}${onDay(decidedAt)}`;
  if (role === Apollo.TaskAssigneeRole.Reviewer)
    return requestedText ? `Reviewing. ${requestedText}` : 'Reviewing';
  return `Task completed${onDay(taskCompletedAt)}`;
}

export interface ReviewRequestAssignment extends ReviewAssignment {
  reviewRequestedBy: { displayName: string; id: UUID } | null;
}

/**
 * The open request, as the assignees' footer names it: "You asked Jane and Lee to review · Mar 3",
 * "Sam asked Jane to review · Mar 3", or "Jane was asked to review · Mar 3" for an automatic one.
 * When a role is among the reviewers it has no name, so the text reads "You requested review ·
 * Mar 3" instead. An external viewer never sees the internal side's names. Null when nobody is
 * reviewing.
 */
export function getReviewRequestText(
  assignments: readonly ReviewRequestAssignment[],
  options: { isExternalView: boolean; viewerUserId: UUID | null }
): string | null {
  const pending = assignments.filter(getIsReviewPending);
  const latest = pending.reduce<ReviewRequestAssignment | null>(
    (found, assignment) =>
      !found || (assignment.reviewRequestedAt ?? '') > (found.reviewRequestedAt ?? '')
        ? assignment
        : found,
    null
  );
  if (!latest?.reviewRequestedAt) return null;

  const date = formatDay(latest.reviewRequestedAt);
  if (options.isExternalView) return `Sent for review · ${date}`;
  const names = getVisibleNames(pending, false);
  const asker = getAskerName(latest, options.viewerUserId);
  if (!names) return asker ? `${asker} requested review · ${date}` : `Review requested · ${date}`;
  const reviewerNames = formatNameList(names);
  if (!asker)
    return `${reviewerNames} ${names.length > 1 ? 'were' : 'was'} asked to review · ${date}`;
  return `${asker} asked ${reviewerNames} to review · ${date}`;
}

/**
 * "You requested review on Mar 3", "Sam requested review on Mar 3", or "Review requested on Mar 3"
 * when the request was automatic.
 */
export function getReviewRequestedText(
  assignment: Pick<ReviewRequestAssignment, 'reviewRequestedAt' | 'reviewRequestedBy'>,
  viewerUserId: UUID | null
): string | null {
  if (!assignment.reviewRequestedAt) return null;
  const date = formatDay(assignment.reviewRequestedAt);
  const asker = getAskerName(assignment, viewerUserId);
  if (!asker) return `Review requested on ${date}`;
  return `${asker} requested review on ${date}`;
}

interface NestableTask {
  id: UUID;
  parentTask: { id: UUID } | null;
}

/**
 * Groups sub-tasks under their parent, in list order. A sub-task whose parent is not in the list
 * (the reader can't see it, or it is complete) stays top level so it is never lost.
 */
export function nestSubtasks<TTask extends NestableTask>(
  tasks: readonly TTask[]
): { subtasks: TTask[]; task: TTask }[] {
  const ids = new Set(tasks.map(task => task.id));
  const getListedParentId = (task: TTask) => {
    const parentId = task.parentTask?.id;
    return parentId && ids.has(parentId) ? parentId : null;
  };
  return tasks
    .filter(task => !getListedParentId(task))
    .map(task => ({
      subtasks: tasks.filter(subtask => getListedParentId(subtask) === task.id),
      task,
    }));
}

/** "Mar 3" */
export function formatDay(iso: string): string {
  return format(parseISO(iso), 'MMM d');
}

function getPendingOrAll(
  assignments: readonly ReviewAssignment[],
  getIsPending: (assignment: ReviewAssignment) => boolean
): ReviewAssignment[] {
  const pending = assignments.filter(getIsPending);
  return pending.length ? pending : [...assignments];
}

/** "you" for the viewer, "Jane (Acme)" for an external, or nothing for a role row. */
function getTurnName(
  assignment: ReviewAssignment,
  options: { counterpartyName: string | null; viewerUserId: UUID | null }
): string[] {
  const user = getAssignmentUser(assignment);
  if (!user) return [];
  if (user.id === options.viewerUserId) return ['you'];
  const { counterpartyName } = options;
  // Someone outside any org is named by their email, which is also the counterparty name.
  const side =
    assignment.isExternal && counterpartyName && counterpartyName !== user.name
      ? ` (${counterpartyName})`
      : '';
  return [`${user.name}${side}`];
}

// Request review until a reviewer has been asked, then Complete.
function getAssigneeFinish(
  assignments: readonly Pick<ReviewAssignment, 'decision' | 'reviewRequestedAt' | 'role'>[]
) {
  if (getHasReviewers(assignments) && !getHasReviewBegun(assignments))
    return FOOTER_ACTION.RequestReview;
  return FOOTER_ACTION.Complete;
}

/** "You" for the viewer, the person's name, or null for an automatic request. */
function getAskerName(
  assignment: Pick<ReviewRequestAssignment, 'reviewRequestedBy'>,
  viewerUserId: UUID | null
): string | null {
  const asker = assignment.reviewRequestedBy;
  if (!asker) return null;
  return asker.id === viewerUserId ? 'You' : asker.displayName;
}

function getReviewerNextStep(options: {
  assignment: Pick<ReviewAssignment, 'decidedAt' | 'decision' | 'reviewRequestedAt'>;
  documentCount: number;
  ownerName: string;
}): YourNextStep {
  const { assignment, documentCount, ownerName } = options;
  if (assignment.decision === Apollo.TaskAssigneeDecision.ChangesRequested)
    return {
      detail:
        "You'll get an email when it's back for your review. Still waiting? Send a reminder below.",
      severity: 'info',
      title: `You requested changes${onDay(assignment.decidedAt)}`,
    };
  if (!assignment.reviewRequestedAt)
    return {
      detail: "You'll get an email when it's ready for your review.",
      severity: 'info',
      title: `${ownerName} is still working on this`,
    };
  if (!documentCount)
    return {
      detail: 'Read the message below, then Approve or Submit changes.',
      severity: 'info',
      title: `${ownerName} requested your review`,
    };
  return {
    detail:
      'Open each document to suggest edits and comment, then Approve or Submit changes below. Prefer Word? Download a document, redline it and upload it back from its card.',
    severity: 'info',
    title: `${ownerName} requested your review of ${documentCount === 1 ? '1 document' : `${documentCount} documents`}`,
  };
}

function getUploadedAssigneeFinishLabel(options: {
  hasReviewers: boolean;
  status: Apollo.TaskStatus;
}): string {
  if (options.status === Apollo.TaskStatus.ChangesRequested) return 'Request Re-Review';
  return FOOTER_ACTION_LABEL[
    options.hasReviewers ? FOOTER_ACTION.RequestReview : FOOTER_ACTION.Complete
  ];
}

/**
 * The names of these people as the viewer may see them, or null when any is unnamed: a role row,
 * or an internal person shown to an external viewer.
 */
function getVisibleNames(
  assignments: readonly Pick<ReviewAssignment, 'isExternal' | 'permission'>[],
  isExternalView: boolean
): string[] | null {
  const names = assignments.flatMap(assignment => {
    const name = getAssignmentUser(assignment)?.name;
    return name && (assignment.isExternal || !isExternalView) ? [name] : [];
  });
  return names.length === assignments.length ? names : null;
}

function onDay(iso: string | null): string {
  return iso ? ` on ${formatDay(iso)}` : '';
}

function formatNameList(names: readonly string[]): string {
  if (names.length > MAX_LISTED_NAMES) {
    const others = names.length - (MAX_LISTED_NAMES - 1);
    return `${names.slice(0, MAX_LISTED_NAMES - 1).join(', ')} and ${others} others`;
  }
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}
